import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

type Loader<T extends ComponentType> = () => Promise<{ default: T }>;

const RETRY_PREFIX = 'agentic360:lazy-retry:';
let chunkRecoveryInstalled = false;

export const lazyReloadRuntime = {
  reloadPage(): void {
    window.location.reload();
  },
};

export function isChunkLoadError(error: unknown): boolean {
  const message = (error instanceof Error ? error.message : String(error ?? '')).toLowerCase();
  return (
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('importing a module script failed') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('failed to load module script') ||
    message.includes('loading chunk') ||
    message.includes('chunkloaderror')
  );
}

function markRetry(key: string): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const retryKey = `${RETRY_PREFIX}${key}`;
  if (window.sessionStorage.getItem(retryKey)) {
    return false;
  }
  window.sessionStorage.setItem(retryKey, '1');
  return true;
}

export function recoverFromChunkLoadError(error: unknown, key = 'global'): boolean {
  if (typeof window === 'undefined' || !isChunkLoadError(error) || !markRetry(key)) {
    return false;
  }
  lazyReloadRuntime.reloadPage();
  return true;
}

export function installChunkLoadRecovery(): void {
  if (typeof window === 'undefined' || chunkRecoveryInstalled) {
    return;
  }
  chunkRecoveryInstalled = true;

  window.addEventListener('vite:preloadError', (event: Event) => {
    // Vite emits this as a CustomEvent. Older Vite shipped the cause via
    // `.payload`, newer Vite uses the standard `.detail` — try both,
    // typed loosely because the DOM lib still doesn't ship a type for it.
    const ce = event as CustomEvent<unknown> & { payload?: unknown };
    const payload = ce.payload ?? ce.detail ?? event;
    if (recoverFromChunkLoadError(payload, 'vite-preload')) {
      event.preventDefault();
    }
  });

  window.addEventListener('error', (event: ErrorEvent) => {
    recoverFromChunkLoadError(event.error ?? event.message, 'window-error');
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    if (recoverFromChunkLoadError(event.reason, 'unhandled-rejection')) {
      event.preventDefault();
    }
  });

  // Clear one-shot global retry markers only after the page finished a clean boot.
  window.addEventListener(
    'load',
    () => {
      window.sessionStorage.removeItem(`${RETRY_PREFIX}vite-preload`);
      window.sessionStorage.removeItem(`${RETRY_PREFIX}window-error`);
      window.sessionStorage.removeItem(`${RETRY_PREFIX}unhandled-rejection`);
    },
    { once: true },
  );
}

export function lazyWithReload<T extends ComponentType>(
  loader: Loader<T>,
  key: string,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const module = await loader();
      if (typeof window !== 'undefined') {
        window.sessionStorage.removeItem(`${RETRY_PREFIX}${key}`);
      }
      return module;
    } catch (error) {
      if (recoverFromChunkLoadError(error, key)) {
        return new Promise<never>(() => {});
      }
      throw error;
    }
  });
}
